import { useState } from 'react'
import type { StandingsProps, StandingsPeriod } from '../types'

const periods: { key: StandingsPeriod; label: string }[] = [
  { key: 'overall', label: 'Overall' },
  { key: 'apertura', label: 'Apertura' },
  { key: 'clausura', label: 'Clausura' },
]

function PeriodTabs({ active, onChange }: {
  active: StandingsPeriod
  onChange: (period: StandingsPeriod) => void
}) {
  return (
    <div className="flex rounded-lg bg-zinc-100 dark:bg-zinc-800 p-0.5">
      {periods.map(({ key, label }) => (
        <button
          key={key}
          onClick={() => onChange(key)}
          className={`flex-1 py-1.5 px-3 text-xs font-semibold rounded-md transition-colors cursor-pointer
                      ${active === key
                        ? 'bg-white dark:bg-zinc-700 text-zinc-900 dark:text-zinc-100 shadow-sm'
                        : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-300'}`}
        >
          {label}
        </button>
      ))}
    </div>
  )
}

function RankCell({ rank, totalManagers }: { rank: number; totalManagers: number }) {
  const isLast = rank === totalManagers && totalManagers > 3

  let colorClass = 'text-zinc-400 dark:text-zinc-500'
  if (rank === 1) colorClass = 'text-amber-500'
  if (rank === 2) colorClass = 'text-zinc-400'
  if (rank === 3) colorClass = 'text-amber-700 dark:text-amber-600'
  if (isLast) colorClass = 'text-red-500 dark:text-red-400'

  return (
    <span className={`text-sm font-bold tabular-nums ${colorClass}`}>
      {rank}
    </span>
  )
}

export function Standings({
  standings,
  currentUserId,
  onManagerSelect,
}: StandingsProps) {
  const [period, setPeriod] = useState<StandingsPeriod>('overall')

  const currentStandings = standings[period]
  const totalManagers = currentStandings.length

  const thClass = 'px-2 py-2 text-[10px] font-semibold uppercase tracking-wider text-zinc-400 dark:text-zinc-500 whitespace-nowrap'

  return (
    <div className="max-w-2xl mx-auto py-6
                    sm:px-6
                    md:px-0">
      {/* Page header */}
      <div className="mb-5">
        <h1 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">
          Standings
        </h1>
      </div>

      {/* Period tabs */}
      <div className="mb-4">
        <PeriodTabs active={period} onChange={setPeriod} />
      </div>

      {/* Table */}
      <div className="rounded-xl border border-zinc-200 dark:border-zinc-800
                      bg-white dark:bg-zinc-900 overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-zinc-100 dark:border-zinc-800">
              <th className={`${thClass} w-12`}></th>
              <th className={`${thClass} text-left`}>Manager</th>
              <th className={`${thClass} text-center`}>Pts</th>
              <th className={`${thClass} text-center`}>MP</th>
              <th className={`${thClass} text-center`}>W</th>
              <th className={`${thClass} text-center`}>D</th>
              <th className={`${thClass} text-center`}>L</th>
              <th className={`${thClass} text-center`}>RC</th>
              <th className={`${thClass} text-center`}>DD</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
            {currentStandings.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-zinc-500 dark:text-zinc-400">
                  No standings data available yet.
                </td>
              </tr>
            ) : (
              currentStandings.map((standing) => {
                const isCurrentUser = standing.managerId === currentUserId
                const isLast = standing.rank === totalManagers && totalManagers > 3

                return (
                  <tr
                    key={standing.managerId}
                    onClick={() => onManagerSelect?.(standing.managerId)}
                    className={`cursor-pointer transition-colors
                                ${isCurrentUser
                                  ? 'bg-emerald-50 dark:bg-emerald-900/20'
                                  : isLast
                                    ? 'bg-red-50/50 dark:bg-red-900/10'
                                    : 'hover:bg-zinc-50 dark:hover:bg-zinc-800/50'}`}
                  >
                    <td className="px-3 py-3">
                      <RankCell rank={standing.rank} totalManagers={totalManagers} />
                    </td>
                    <td className={`px-2 py-3 text-sm whitespace-nowrap
                                    ${isCurrentUser
                                      ? 'font-bold text-emerald-700 dark:text-emerald-300'
                                      : 'font-medium text-zinc-900 dark:text-zinc-100'}`}>
                      {standing.displayName}
                    </td>
                    <td className={`px-2 py-3 text-center text-sm tabular-nums font-bold
                                    ${isCurrentUser
                                      ? 'text-emerald-700 dark:text-emerald-300'
                                      : 'text-zinc-900 dark:text-zinc-100'}`}>
                      {standing.points}
                    </td>
                    <td className="px-2 py-3 text-center text-sm tabular-nums font-medium text-zinc-400 dark:text-zinc-500">
                      {standing.matchesPlayed}
                    </td>
                    <td className="px-2 py-3 text-center text-sm tabular-nums font-medium text-emerald-600 dark:text-emerald-400">
                      {standing.wins}
                    </td>
                    <td className="px-2 py-3 text-center text-sm tabular-nums font-medium text-zinc-500 dark:text-zinc-400">
                      {standing.draws}
                    </td>
                    <td className="px-2 py-3 text-center text-sm tabular-nums font-medium text-red-500 dark:text-red-400">
                      {standing.losses}
                    </td>
                    <td className="px-2 py-3 text-center text-sm tabular-nums font-medium text-zinc-400 dark:text-zinc-500">
                      {standing.redCards}
                    </td>
                    <td className="px-2 py-3 text-center text-sm tabular-nums font-medium text-violet-500 dark:text-violet-400">
                      {standing.ddUsed}
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
